import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Switch, Select, Button, Space } from "antd";
import { StarOutlined, CrownOutlined } from "@ant-design/icons";
import { IVipTier, ICreateVipTierRequest, IUpdateVipTierRequest } from "../../common/vip.interface";

interface CModalVipTierProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (data: ICreateVipTierRequest | IUpdateVipTierRequest) => void;
    editData?: IVipTier | null;
    existingTiers?: IVipTier[];
}

const CModalVipTier: React.FC<CModalVipTierProps> = ({
    visible,
    onCancel,
    onSubmit,
    editData,
    existingTiers = []
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible) {
            if (editData) {
                // Edit mode
                form.setFieldsValue({
                    name: editData.name,
                    description: editData.description,
                    requiredPoints: editData.requiredPoints,
                    discountPercent: editData.discountPercent,
                    maxDiscount: editData.maxDiscount,
                    color: editData.color,
                    level: editData.level,
                    isActive: editData.isActive,
                });
            } else {
                // Add mode - reset form and set defaults
                form.resetFields();
                form.setFieldsValue({
                    isActive: true,
                    discountPercent: 5,
                });
            }
        }
    }, [visible, editData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            onSubmit(values);
            setLoading(false);
        } catch (error) {
            console.error('Validation failed:', error);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // Get used levels excluding current tier if editing
    const usedLevels = existingTiers
        .filter(tier => editData ? tier.id !== editData.id : true)
        .map(tier => tier.level);

    const levelValidator = (_: any, value: number) => {
        if (!value) {
            return Promise.reject(new Error('Vui lòng nhập cấp độ'));
        }
        if (value < 1) {
            return Promise.reject(new Error('Cấp độ phải lớn hơn 0'));
        }
        if (usedLevels.includes(value)) {
            return Promise.reject(new Error('Cấp độ này đã được sử dụng'));
        }
        return Promise.resolve();
    };

    const requiredPointsValidator = (_: any, value: number) => {
        if (value === undefined || value === null) {
            return Promise.reject(new Error('Vui lòng nhập số điểm yêu cầu'));
        }
        if (value < 0) {
            return Promise.reject(new Error('Số điểm phải lớn hơn hoặc bằng 0'));
        }
        return Promise.resolve();
    };

    const discountPercentValidator = (_: any, value: number) => {
        if (value === undefined || value === null) {
            return Promise.reject(new Error('Vui lòng nhập phần trăm giảm giá'));
        }
        if (value < 0 || value > 100) {
            return Promise.reject(new Error('Phần trăm giảm giá phải từ 0 đến 100'));
        }
        return Promise.resolve();
    };

    const maxDiscountValidator = (_: any, value: number) => {
        if (value !== undefined && value !== null && value < 0) {
            return Promise.reject(new Error('Số tiền giảm tối đa phải lớn hơn hoặc bằng 0'));
        }
        return Promise.resolve();
    };

    return (
        <Modal
            title={
                <Space>
                    <CrownOutlined style={{ color: "#faad14" }} />
                    <span>{editData ? "Chỉnh sửa cấp độ VIP" : "Thêm cấp độ VIP mới"}</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Hủy
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {editData ? "Cập nhật" : "Thêm mới"}
                </Button>
            ]}
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 20 }}
            >
                <Form.Item
                    name="name"
                    label="Tên cấp độ VIP"
                    rules={[
                        { required: true, message: "Vui lòng nhập tên cấp độ VIP" },
                        { max: 255, message: "Tên không được vượt quá 255 ký tự" }
                    ]}
                >
                    <Input 
                        placeholder="VD: VIP Bronze, VIP Silver..." 
                        prefix={<StarOutlined />}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Mô tả"
                    rules={[
                        { max: 500, message: "Mô tả không được vượt quá 500 ký tự" }
                    ]}
                >
                    <Input.TextArea 
                        placeholder="Mô tả về cấp độ VIP này..."
                        rows={3}
                    />
                </Form.Item>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item
                        name="level"
                        label="Cấp độ"
                        style={{ flex: 1 }}
                        rules={[{ validator: levelValidator }]}
                    >
                        <InputNumber
                            min={1}
                            placeholder="1"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="requiredPoints"
                        label="Điểm yêu cầu"
                        style={{ flex: 1 }}
                        rules={[{ validator: requiredPointsValidator }]}
                    >
                        <InputNumber
                            min={0}
                            placeholder="5000"
                            style={{ width: '100%' }}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                        />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item
                        name="discountPercent"
                        label="Phần trăm giảm giá (%)"
                        style={{ flex: 1 }}
                        rules={[{ validator: discountPercentValidator }]}
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            placeholder="5"
                            style={{ width: '100%' }}
                            addonAfter="%"
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxDiscount"
                        label="Giảm tối đa (VNĐ)"
                        style={{ flex: 1 }}
                        rules={[{ validator: maxDiscountValidator }]}
                    >
                        <InputNumber
                            min={0}
                            placeholder="50000"
                            style={{ width: '100%' }}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                        />
                    </Form.Item>
                </div>

                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                    <Form.Item
                        name="color"
                        label="Màu hiển thị"
                        style={{ flex: 1 }}
                    >
                        <Input placeholder="#FFD700" />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Trạng thái"
                        valuePropName="checked"
                        style={{ flex: 1 }}
                    >
                        <Switch 
                            checkedChildren="Hoạt động" 
                            unCheckedChildren="Tạm ngưng"
                        />
                    </Form.Item>
                </div>
            </Form>

            <div style={{ 
                marginTop: 20, 
                padding: 16, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 8,
                fontSize: '13px',
                color: '#666'
            }}>
                <strong>Lưu ý:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>Cấp độ VIP phải là số duy nhất và lớn hơn 0</li>
                    <li>Số điểm yêu cầu nên tăng dần theo cấp độ</li>
                    <li>Phần trăm giảm giá từ 0% đến 100%</li>
                    <li>Giảm tối đa giúp kiểm soát chi phí (để trống = không giới hạn)</li>
                </ul>
            </div>
        </Modal>
    );
};

export default CModalVipTier;
